import React from "react";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { Trash2, Minus, Plus } from "lucide-react";
import { useCart } from "../context/CartContext";
import { formatUAH } from "../lib/format";

export const CartDrawer = () => {
  const { items, removeItem, updateQty, total, open, setOpen } = useCart();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-silea-cream border-l border-stone-200 flex flex-col p-0"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-6 py-6 border-b border-stone-200">
          <SheetTitle className="font-serif-display text-2xl text-silea-navy text-left">
            Ваш кошик
          </SheetTitle>
          <p className="eyebrow text-silea-navy/60 text-left">{items.length} {items.length === 1 ? "позиція" : "позицій"}</p>
        </SheetHeader>

        <div className="flex-1 overflow-auto px-6 py-4 space-y-6">
          {items.length === 0 && (
            <div className="text-center py-20">
              <p className="font-serif-display italic text-2xl text-silea-navy mb-3">Ваш кошик порожній</p>
              <p className="text-sm text-silea-navy/60 mb-6">Знайдіть свій ідеальний образ для літа.</p>
              <Button
                onClick={() => setOpen(false)}
                asChild
                className="bg-silea-navy hover:bg-silea-navy/90 text-white rounded-none"
              >
                <Link to="/products" data-testid="cart-empty-shop-btn">Перейти до колекції</Link>
              </Button>
            </div>
          )}

          {items.map((it) => (
            <div key={it._key} className="flex gap-4" data-testid="cart-line">
              <div className="w-20 h-28 bg-stone-100 overflow-hidden flex-shrink-0">
                <img src={it.image} alt={it.product_name} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between gap-2">
                  <div>
                    <p className="font-serif-display text-lg leading-tight text-silea-navy">{it.product_name}</p>
                    <p className="text-xs text-silea-navy/60 mt-1">
                      {it.color} · {it.size}
                    </p>
                  </div>
                  <button
                    onClick={() => removeItem(it._key)}
                    data-testid="cart-remove-btn"
                    className="text-silea-navy/40 hover:text-silea-navy"
                  >
                    <Trash2 size={16} strokeWidth={1.4} />
                  </button>
                </div>
                {it.customizations.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {it.customizations.map((c, i) => (
                      <li key={i} className="text-xs text-silea-navy/70">
                        + {c.label}{c.custom_text ? ` "${c.custom_text}"` : ""} <span className="text-silea-navy/50">({formatUAH(c.extra_price)})</span>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex items-center justify-between mt-3">
                  <div className="inline-flex items-center border border-stone-300">
                    <button
                      onClick={() => updateQty(it._key, Math.max(1, it.quantity - 1))}
                      className="px-2 py-1 text-silea-navy"
                      data-testid="cart-decrease-btn"
                    >
                      <Minus size={12} />
                    </button>
                    <span className="px-3 text-sm">{it.quantity}</span>
                    <button
                      onClick={() => updateQty(it._key, it.quantity + 1)}
                      className="px-2 py-1 text-silea-navy"
                      data-testid="cart-increase-btn"
                    >
                      <Plus size={12} />
                    </button>
                  </div>
                  <p className="text-silea-navy font-medium">{formatUAH(it.line_total)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          <div className="border-t border-stone-200 px-6 py-6 space-y-4 bg-white">
            <div className="flex justify-between items-baseline">
              <span className="eyebrow text-silea-navy/60">Разом</span>
              <span className="font-serif-display text-2xl text-silea-navy" data-testid="cart-total">
                {formatUAH(total)}
              </span>
            </div>
            <Button
              asChild
              className="w-full bg-silea-navy hover:bg-silea-navy/90 text-white rounded-none h-12 eyebrow"
              onClick={() => setOpen(false)}
            >
              <Link to="/checkout" data-testid="checkout-cta">Оформити замовлення</Link>
            </Button>
            <p className="text-[11px] text-silea-navy/50 text-center">
              Оплата за реквізитами після підтвердження замовлення
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
